/**
 * AppSumo deal bullets, grouped the way the buyer thinks about them:
 * what recycles, what is granted once, what is never metered, and what is a
 * workspace-wide cap. Driven by APPSUMO_TIER_LIMITS so the copy can never
 * drift from what applyAppSumoEntitlements actually grants.
 *
 * Client-safe (same rule as tier-limits.ts): no keys(), no server-only, no
 * database imports — plan cards render straight from it.
 */

import {
  formatMinutesLimit,
  formatStorageLimit,
  formatVideoLength
} from '../limits-config';
import {
  APPSUMO_AVATAR_CREDITS_PER_MINUTE,
  APPSUMO_TIER_LIMITS,
  isAppSumoTier
} from './tier-limits';

export type AppSumoDealGroups = {
  /** Recycles on the activation anniversary. */
  monthly: string[];
  /** Granted once for the life of the license. */
  oneTime: string[];
  /** Never metered on this plan source. */
  included: string[];
  /** Workspace-wide caps (storage, seats, per-video length). */
  workspace: string[];
};

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count !== 1 ? 's' : ''}`;
}

export function getAppSumoDealGroups(
  appsumoTier: number | null | undefined
): AppSumoDealGroups {
  if (!isAppSumoTier(appsumoTier)) {
    return {
      monthly: [],
      oneTime: [],
      included: ['Lifetime access with no recurring subscription'],
      workspace: []
    };
  }

  const deal = APPSUMO_TIER_LIMITS[appsumoTier];
  const avatarRateNote = `${APPSUMO_AVATAR_CREDITS_PER_MINUTE.NORMAL}–${APPSUMO_AVATAR_CREDITS_PER_MINUTE.HIGH} credits/min by quality`;

  return {
    monthly: [
      `${formatMinutesLimit(deal.monthly.uploadMinutes)} of video upload / month`,
      `${plural(deal.monthly.cloudRenders, 'cloud render')} / month`
    ],
    oneTime: [
      `${formatMinutesLimit(deal.oneTime.videoAnalysisMinutes)} of video analysis (one-time)`,
      `${deal.oneTime.avatarMinutes} AI avatar credits (one-time, ${avatarRateNote})`,
      `${plural(deal.oneTime.translations, 'translation')} (one-time)`,
      ...(deal.oneTime.photoAvatarTrainings > 0
        ? [
            `${plural(deal.oneTime.photoAvatarTrainings, 'photo avatar training')} (one-time)`
          ]
        : []),
      ...(deal.oneTime.digitalTwinTrainings > 0
        ? [
            `${plural(deal.oneTime.digitalTwinTrainings, 'digital twin training')} (one-time)`
          ]
        : [])
    ],
    included: [
      'Unlimited local renders',
      'Unlimited AI scripts',
      'Unlimited standard AI voice',
      'No watermark'
    ],
    workspace: [
      `${formatStorageLimit(deal.maxStorageBytes)} storage`,
      deal.seats === 1 ? '1 user' : `Up to ${deal.seats} team members`,
      `${formatVideoLength(deal.maxVideoLengthSeconds)} max / video`
    ]
  };
}
